import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { Menu, Box, IconButton, Typography } from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem, treeItemClasses } from "@mui/x-tree-view/TreeItem";
import classes from "./VirtualDirectory.module.css";
import Tooltip, { ICON_NONE } from "../UI/Tooltip/Tooltip";

const CustomTreeItem = styled(TreeItem)(() => ({
  [`& .${treeItemClasses.content}`]: {
    backgroundColor: "white",
    alignItems: "flex-start",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  [`& .${treeItemClasses.iconContainer}`]: {
    marginTop: "3px",
  },
  [`& .${treeItemClasses.content}.Mui-selected,
    & .${treeItemClasses.content}.Mui-focused,
    & .${treeItemClasses.content}.Mui-selected.Mui-focused,
    & .${treeItemClasses.content}.Mui-selected:hover,
    & .${treeItemClasses.content}:focus,
    & .${treeItemClasses.content}:focus-visible
  `]: {
    backgroundColor: "white !important",
  },
}));

const VirtualDirectory = (props) => {
  const {
    row,
    anchorEl,
    setAnchorEl,
    handleClickDirectory,
    rowFilters,
    resetRowFilters,
    allExpandableNodeIds,
    setAllExpandableNodeIds,
    honeycomb,
  } = props;
  const [directoryTree, setDirectoryTree] = useState(null); // tree component
  const [expandedIds, setExpandedIds] = useState([]);
  const [treeWidth, setTreeWidth] = useState(null);
  const isAllCollapsed = expandedIds.length === 0;
  const isAllExpanded = expandedIds.length === allExpandableNodeIds.length;
  const style = {
    lakeIcon: {
      color: classes.lake500,
    },
    greyIcon: {
      color: classes.grey500,
    },
    iconButton: {
      padding: "4px",
    },
    greenIcon: {
      color: classes.moss400,
      fontSize: "32px",
    },
    disabledIcon: {
      color: classes.grey600,
      fontSize: "32px",
    },
    paragraph: {
      paddingTop: 20,
      marginTop: 0,
      paddingLeft: 10,
      paddingRight: 10,
      width: treeWidth,
    },
    treeView: {
      maxHeight: 240,
      flexGrow: 1,
      width: "fit-content",
      overflowY: "auto",
      minWidth: 250,
    },
    treeItemLabelSelected: {
      backgroundColor: classes.lake25,
      textDecoration: "none",
    },
    treeItemLabelUnselected: {
      textDecoration: "underline",
    },
    menu: {
      maxWidth: 400,
      minWidth: 200,
    },
    resetButton: {
      color: "black",
      "&:hover": {
        color: "black",
      },
      fontSize: 16,
      cursor: "pointer",
    },
    organismName: {},
  };

  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
    const honeycombPayload = {
      genome_id: row.id,
    };
    honeycomb.sendUiInteractionSpan("clicked-vds-close", honeycombPayload);
  };

  const getTreeItemStyle = (node) => {
    const isSelected = rowFilters.has(node);
    if (isSelected) return style.treeItemLabelSelected;
    return style.treeItemLabelUnselected;
  };

  const generateTree = (node, path = [], updateExpandableIds) => {
    if (Object.keys(node).length === 0) return null;
    return Object.keys(node)
      .sort()
      .map((key) => {
        const value = node[key];
        const newPath = [...path];
        newPath.push(key);
        if (
          updateExpandableIds &&
          value &&
          typeof value.children === "object" &&
          Object.keys(value.children).length > 0
        )
          setAllExpandableNodeIds((prev) => [...prev, newPath.join("/")]);
        return (
          <CustomTreeItem
            key={newPath.join("/")}
            label={
              <Typography
                sx={getTreeItemStyle(newPath.join("/"))}
                onClick={(e) => handleClickDirectory(e, newPath.join("/"))}
                className={classes.treeItem}
              >
                {key}
              </Typography>
            }
            nodeId={newPath.join("/")}
          >
            {generateTree(value?.children, newPath, updateExpandableIds)}
          </CustomTreeItem>
        );
      });
  };

  const combineTrees = (tree1, tree2) => {
    const result = tree1;
    Object.keys(tree2).forEach((key) => {
      if (key in result) {
        if (typeof tree2[key] === "object" && typeof result[key] === "object") {
          result[key] = combineTrees(result[key], tree2[key]);
        } else {
          result[key] = tree2[key];
        }
      } else if (typeof tree2[key] === "object") {
        result[key] = JSON.parse(JSON.stringify(tree2[key]));
      } else {
        result[key] = tree2[key];
      }
    });
    return result;
  };

  useEffect(() => {
    if (row) {
      let newDirectoryTree = {};
      row?.files.forEach((file) => {
        let path;
        if (row.kingdom === "phytozome") path = file?.metadata?.type.split("/");
        else path = file?.metadata?.portal?.display_location;
        const newDirectoryNode = {};
        if (Array.isArray(path)) {
          let newTree = newDirectoryNode;
          path.forEach((directory, idx) => {
            newTree[directory] = {
              level: idx,
              children: {},
            };
            newTree = newTree[directory].children;
          });
        }
        newDirectoryTree = combineTrees(newDirectoryTree, newDirectoryNode);
      });
      const updateExpandableIds = allExpandableNodeIds.length === 0;
      setDirectoryTree(generateTree(newDirectoryTree, [], updateExpandableIds));
    }
  }, [anchorEl, rowFilters]);

  const setWidth = () => {
    if (directoryTree && anchorEl) {
      const treeViewElement = document.getElementById("tree-view");
      const boundingBox = treeViewElement?.getBoundingClientRect();
      const width = boundingBox?.width;
      if (width) setTreeWidth(width);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setWidth();
    }, 500);
  }, [directoryTree, expandedIds]);

  useEffect(() => {
    if (anchorEl) {
      const newExpandedIds = new Set();
      rowFilters.forEach((rowFilter) => {
        let fullPath = "";
        rowFilter.split("/").forEach((node, idx) => {
          if (idx !== 0) fullPath += "/";
          fullPath += node;
          if (allExpandableNodeIds.includes(fullPath))
            return newExpandedIds.add(fullPath);
        });
      });
      setExpandedIds([...newExpandedIds]);
    }
  }, [anchorEl]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      keepMounted
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        style: style.menu,
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        px={1}
      >
        <Tooltip placement="right" title="Expand all" iconType={ICON_NONE}>
          <span>
            <IconButton
              onClick={() => setExpandedIds(allExpandableNodeIds)}
              sx={style.iconButton}
              disabled={isAllExpanded}
              data-testid="vds-expand-all"
            >
              <AddCircleIcon
                sx={isAllExpanded ? style.disabledIcon : style.greenIcon}
              />
            </IconButton>
          </span>
        </Tooltip>

        <span
          onClick={resetRowFilters}
          style={style.resetButton}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              resetRowFilters();
            }
          }}
          tabIndex={0}
          role="button"
        >
          <u>Clear All</u>
        </span>
        <Tooltip placement="left" title="Collapse all" iconType={ICON_NONE}>
          <span>
            <IconButton
              onClick={() => setExpandedIds([])}
              sx={style.iconButton}
              disabled={isAllCollapsed}
              data-testid="vds-collapse-all"
            >
              <RemoveCircleIcon
                sx={isAllCollapsed ? style.disabledIcon : style.greenIcon}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <TreeView
        defaultCollapseIcon={<IndeterminateCheckBoxIcon sx={style.lakeIcon} />}
        defaultExpandIcon={<AddBoxIcon sx={style.lakeIcon} />}
        defaultEndIcon={<DisabledByDefaultIcon sx={style.greyIcon} />}
        sx={style.treeView}
        expanded={expandedIds}
        onNodeToggle={(_, nodeIds) => setExpandedIds(nodeIds)}
        id="tree-view"
      >
        {directoryTree}
      </TreeView>
      <Box sx={style.organismName}>
        {treeWidth && (
          <p style={style.paragraph} className={classes.h6Desktop}>
            {row?.name}
          </p>
        )}
      </Box>
    </Menu>
  );
};

export default VirtualDirectory;

VirtualDirectory.defaultProps = {
  row: {},
  honeycomb: {
    sendUiInteractionSpan: () => {},
  },
  anchorEl: null,
  setAnchorEl: null,
  rowFilters: () => new Set(),
  resetRowFilters: () => {},
  handleClickDirectory: () => {},
  allExpandableNodeIds: [],
  setAllExpandableNodeIds: () => {},
};

VirtualDirectory.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    name: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.shape()),
    kingdom: PropTypes.string,
    portal_detail_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    pi: PropTypes.shape({
      name: PropTypes.string,
      email_address: PropTypes.string,
    }),
    proposal_acceptance_date: PropTypes.string,
    work_completion_date: PropTypes.string,
    dus_status: PropTypes.string,
    eco_data: PropTypes.shape(),
    gold_proj_data: PropTypes.shape(),
    superseded_by: PropTypes.string,
    proposal_lay_description: PropTypes.string,
    agg_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pubs: PropTypes.arrayOf(PropTypes.shape()),
  }),
  honeycomb: PropTypes.shape({
    sendUiInteractionSpan: PropTypes.func,
  }),
  anchorEl: PropTypes.shape(),
  setAnchorEl: PropTypes.func,
  rowFilters: PropTypes.objectOf(PropTypes.string),
  resetRowFilters: PropTypes.func,
  handleClickDirectory: PropTypes.func,
  allExpandableNodeIds: PropTypes.arrayOf(PropTypes.string),
  setAllExpandableNodeIds: PropTypes.func,
};
